if (!customElements.get("ritual-hotspots")) {
  class RitualHotspots extends HTMLElement {
    constructor() {
      super();
      this.spots = [];
      this.activeId = null;
      this.cartDrawer = null;
      this.cartUpdateEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) ||
        "cart-update";
      this.cartErrorEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartError) ||
        "cart-error";
      this.activeOpener = null;
      this.sheetOpen = false;
      this.mobileQuery = window.matchMedia("(max-width: 767px)");
      this.onSpotToggle = this.onSpotToggle.bind(this);
      this.onSpotClose = this.onSpotClose.bind(this);
      this.onAdd = this.onAdd.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onEditorBlockSelect = this.onEditorBlockSelect.bind(this);
      this.onEditorBlockDeselect = this.onEditorBlockDeselect.bind(this);
      this.onSheetClose = this.onSheetClose.bind(this);
    }

    connectedCallback() {
      this.spots = Array.from(this.querySelectorAll("[data-spot]"));
      this.sheet = this.querySelector("[data-spot-sheet]");
      this.sheetPanel = this.querySelector("[data-spot-sheet-panel]");
      this.sheetContent = this.querySelector("[data-spot-sheet-content]");
      this.spots.forEach((spot) => {
        const trigger = spot.querySelector("[data-spot-trigger]");
        const close = spot.querySelector("[data-spot-close]");
        const add = spot.querySelector("[data-spot-add]");
        trigger?.addEventListener("click", this.onSpotToggle);
        close?.addEventListener("click", this.onSpotClose);
        add?.addEventListener("click", this.onAdd);
      });
      document.addEventListener("click", this.onDocumentClick);
      this.addEventListener("keydown", this.onKeyDown);
      const sheetClose = this.querySelector("[data-spot-sheet-close]");
      const sheetOverlay = this.querySelector("[data-spot-sheet-overlay]");
      sheetClose?.addEventListener("click", this.onSheetClose);
      sheetOverlay?.addEventListener("click", this.onSheetClose);
      if (window.Shopify && Shopify.designMode) {
        document.addEventListener("shopify:block:select", this.onEditorBlockSelect);
        document.addEventListener("shopify:block:deselect", this.onEditorBlockDeselect);
      }
    }

    disconnectedCallback() {
      this.spots.forEach((spot) => {
        const trigger = spot.querySelector("[data-spot-trigger]");
        const close = spot.querySelector("[data-spot-close]");
        const add = spot.querySelector("[data-spot-add]");
        trigger?.removeEventListener("click", this.onSpotToggle);
        close?.removeEventListener("click", this.onSpotClose);
        add?.removeEventListener("click", this.onAdd);
      });
      const sheetClose = this.querySelector("[data-spot-sheet-close]");
      const sheetOverlay = this.querySelector("[data-spot-sheet-overlay]");
      sheetClose?.removeEventListener("click", this.onSheetClose);
      sheetOverlay?.removeEventListener("click", this.onSheetClose);
      document.removeEventListener("click", this.onDocumentClick);
      this.removeEventListener("keydown", this.onKeyDown);
      document.removeEventListener("shopify:block:select", this.onEditorBlockSelect);
      document.removeEventListener("shopify:block:deselect", this.onEditorBlockDeselect);
    }

    onSpotToggle(event) {
      event.stopPropagation();
      const spot = event.currentTarget?.closest("[data-spot]");
      if (!spot) return;
      const trigger = spot.querySelector("[data-spot-trigger]");
      const id = spot.dataset.spotId;
      if (this.mobileQuery.matches) {
        this.activeOpener = trigger;
        this.openSheet(spot);
        return;
      }
      if (this.activeId === id) {
        this.setActive(null);
        return;
      }
      this.activeOpener = trigger;
      this.setActive(id);
      const card = spot.querySelector("[data-spot-card]");
      if (card) {
        this.trapCardFocus(card);
        const focusable = card.querySelector("a, button, [tabindex]");
        if (focusable) focusable.focus();
      }
    }

    onSpotClose(event) {
      event.stopPropagation();
      const opener = this.activeOpener;
      this.setActive(null);
      if (opener) opener.focus();
    }

    onKeyDown(event) {
      if (event.key !== "Escape") return;
      if (this.sheetOpen) {
        this.closeSheet();
        return;
      }
      if (this.activeId) {
        const opener = this.activeOpener;
        this.setActive(null);
        if (opener) opener.focus();
      }
    }

    onDocumentClick(event) {
      if (this.sheetOpen) return;
      if (this.contains(event.target)) return;
      this.setActive(null);
    }

    onEditorBlockSelect(event) {
      const blockId = event?.detail?.blockId;
      if (!blockId) return;
      const spot = this.spots.find((s) => s.dataset.spotId === blockId);
      if (!spot) return;
      this.setActive(blockId);
    }

    onEditorBlockDeselect() {
      this.setActive(null);
    }

    onSheetClose(event) {
      event.stopPropagation();
      this.closeSheet();
    }

    openSheet(spot) {
      if (!this.sheet || !this.sheetContent || !this.sheetPanel) return;
      const card = spot.querySelector("[data-spot-card]");
      if (!card) return;
      this.sheetContent.innerHTML = card.querySelector(":scope > div")?.outerHTML || "";
      const sheetClose = this.sheetContent.querySelector("[data-spot-close]");
      if (sheetClose) sheetClose.remove();
      const overlay = this.querySelector("[data-spot-sheet-overlay]");
      this.sheet.classList.remove("hidden");
      this.sheet.setAttribute("aria-hidden", "false");
      requestAnimationFrame(() => {
        this.sheetPanel.classList.remove("translate-y-full");
        if (overlay) {
          overlay.classList.add("opacity-100");
          overlay.classList.remove("opacity-0");
        }
      });
      document.documentElement.classList.add("overflow-hidden");
      this.sheetOpen = true;
      if (typeof webvista?.createFocusTrap === "function") {
        this._sheetFocusTrap = webvista.createFocusTrap(this.sheet, {
          onEscape: () => this.closeSheet()
        });
        this.sheet.addEventListener("keydown", this._sheetFocusTrap);
      }
      const focusable = this.sheetPanel.querySelector("a, button, [tabindex]");
      if (focusable) focusable.focus();
    }

    closeSheet() {
      if (!this.sheet || !this.sheetPanel) return;
      const overlay = this.querySelector("[data-spot-sheet-overlay]");
      this.sheetPanel.classList.add("translate-y-full");
      if (overlay) {
        overlay.classList.remove("opacity-100");
        overlay.classList.add("opacity-0");
      }
      this.sheet.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("overflow-hidden");
      if (this._sheetFocusTrap) {
        this.sheet.removeEventListener("keydown", this._sheetFocusTrap);
        this._sheetFocusTrap = null;
      }
      const opener = this.activeOpener;
      this.sheetOpen = false;
      setTimeout(() => {
        this.sheet.classList.add("hidden");
        if (this.sheetContent) this.sheetContent.innerHTML = "";
      }, 700);
      if (opener) opener.focus();
    }

    trapCardFocus(card) {
      this.releaseCardFocus();
      if (typeof webvista?.createFocusTrap === "function") {
        this._cardFocusTrap = webvista.createFocusTrap(card, {
          onEscape: () => {
            const opener = this.activeOpener;
            this.setActive(null);
            if (opener) opener.focus();
          }
        });
        card.addEventListener("keydown", this._cardFocusTrap);
        this._cardFocusTrapEl = card;
      }
    }

    releaseCardFocus() {
      if (this._cardFocusTrap && this._cardFocusTrapEl) {
        this._cardFocusTrapEl.removeEventListener("keydown", this._cardFocusTrap);
      }
      this._cardFocusTrap = null;
      this._cardFocusTrapEl = null;
    }

    setActive(id) {
      if (!id) this.releaseCardFocus();
      this.activeId = id;
      this.spots.forEach((spot) => {
        const isActive = spot.dataset.spotId === id;
        const trigger = spot.querySelector("[data-spot-trigger]");
        const ring = spot.querySelector("[data-spot-ring]");
        const core = spot.querySelector("[data-spot-core]");
        const card = spot.querySelector("[data-spot-card]");
        if (trigger) {
          trigger.setAttribute("aria-expanded", isActive ? "true" : "false");
        }
        if (ring) {
          ring.classList.toggle("opacity-0", isActive);
          ring.classList.toggle("opacity-100", !isActive);
        }
        if (core) {
          core.classList.toggle("scale-150", isActive);
          core.classList.toggle("bg-accent", isActive);
          core.classList.toggle("shadow-[0_0_15px_rgba(var(--color-background),0.6)]", !isActive);
        }
        if (card) {
          card.classList.toggle("hidden", !isActive);
          card.classList.toggle("animate-hotspot-fade-in-up", isActive);
        }
      });
    }

    getSectionsToRender() {
      const drawer = document.getElementById("Cart-Drawer");
      const drawerSectionId = drawer?.dataset?.sectionId || "cart-drawer";
      return [
        {
          id: "Cart-Icon-Bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section"
        },
        {
          id: "Cart-Drawer",
          section: drawerSectionId,
          selector: "[data-cart-drawer-content]"
        }
      ];
    }

    renderSections(sections = {}) {
      this.getSectionsToRender().forEach((section) => {
        const html = sections[section.section];
        if (!html) return;
        const container = document.getElementById(section.id);
        if (!container) return;
        if (!section.selector) {
          container.innerHTML = html;
          return;
        }
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const element = parsed.querySelector(section.selector);
        if (element) {
          if (section.id === "Cart-Drawer") {
            const contentTarget = container.querySelector(
              "[data-cart-drawer-content]"
            );
            const summaryTarget = container.querySelector(
              "[data-cart-drawer-summary]"
            );
            const progressTarget = container.querySelector(
              "free-shipping-progress"
            );
            if (contentTarget) {
              contentTarget.innerHTML = element.innerHTML;
              if (window.webvista?.initLazyImageAnimation) {
                window.webvista.initLazyImageAnimation(contentTarget);
              }
            }
            const summaryElement = parsed.querySelector(
              "[data-cart-drawer-summary]"
            );
            if (summaryTarget && summaryElement) {
              summaryTarget.innerHTML = summaryElement.innerHTML;
              summaryTarget.className = summaryElement.className;
            }
            const progressSource = parsed.querySelector(
              "free-shipping-progress"
            );
            if (progressTarget && progressSource) {
              progressTarget.dataset.totalAmount =
                progressSource.dataset.totalAmount || "";
            }
            if (typeof container.refreshUpsell === "function") {
              container.refreshUpsell();
            }
            return;
          }
          container.innerHTML = element.innerHTML;
          return;
        }
        container.innerHTML = html;
      });
    }

    async openCartDrawer(opener) {
      const header = document.querySelector("header-component");
      const cartType = header?.dataset.cartType || "drawer";
      const cartOpenOnAdd = header?.dataset.cartOpenOnAdd !== "false";
      if (!cartOpenOnAdd) return;
      if (cartType === "page") {
        window.location.href = window.routes?.cart_url || "/cart";
        return;
      }
      if (!this.cartDrawer) {
        this.cartDrawer = document.getElementById("Cart-Drawer");
      }
      if (!this.cartDrawer) return;
      if (typeof this.cartDrawer.show === "function") {
        this.cartDrawer.show(opener);
        return;
      }
      await customElements.whenDefined("cart-drawer");
      if (typeof this.cartDrawer.show === "function") {
        this.cartDrawer.show(opener);
      }
    }

    async onAdd(event) {
      event.preventDefault();
      event.stopPropagation();
      const button = event.currentTarget;
      const variantId = button?.dataset?.variantId;
      if (!variantId) return;
      button.setAttribute("aria-busy", "true");
      button.setAttribute("disabled", "disabled");
      try {
        const sections = this.getSectionsToRender().map(
          (section) => section.section
        );
        const config = webvista.fetchConfig("json", "POST");
        config.body = JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname
        });
        const response = await fetch("/cart/add.js", config);
        if (!response.ok) {
          const errorData = await response.json();
          document.dispatchEvent(
            new CustomEvent(this.cartErrorEvent, { detail: { error: errorData } })
          );
          throw new Error("Add failed");
        }
        const data = await response.json();
        if (data?.sections) {
          this.renderSections(data.sections);
        }
        await this.openCartDrawer(button);
        document.dispatchEvent(
          new CustomEvent(this.cartUpdateEvent, {
            detail: { variantId }
          })
        );
      } catch (error) {
        console.error(error);
      } finally {
        button.removeAttribute("aria-busy");
        button.removeAttribute("disabled");
      }
    }
  }

  customElements.define("ritual-hotspots", RitualHotspots);
}
